/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import {
  colors,
  Spacer,
  Menu,
  MenuItem,
  TypeRampV2,
  sizes,
} from '@smartsheet/lodestar-core';
import { KebabIcon, CloseIcon } from '@smartsheet/lodestar-icons';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PANEL_SPACE_BOTTOM,
  PANEL_HEADER_HEIGHT,
  PANEL_HEADER_SPACE_BOTTOM,
  PANEL_SPACE_TOP,
  PANEL_WIDTH,
} from '../../../utils/dimensions';
import { Column } from '../Column';
import { VerticalBar } from './styled';
import { PanelButton } from './styled/PanelButton';

interface PanelProps {
  right: number;
  children: ReactNode;
  closePanel: Function;
  isShown?: boolean;
}

const panelItemsSpacingStyle = css`
  justify-content: space-between;
  margin-bottom: ${PANEL_HEADER_SPACE_BOTTOM}px;
  height: ${PANEL_HEADER_HEIGHT}px;
`;

const useOutsideAlerter = (
  ref: React.MutableRefObject<any>,
  closePanel: Function
): void => {
  useEffect(() => {
    const handleClickOutside = (event: { target: any }): void => {
      if (
        ref.current &&
        ref.current.clientWidth > 0 &&
        !ref.current.contains(event.target) &&
        event.target.parentNode.closest('#canvas')
      ) {
        closePanel();
      }
    };
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [ref, closePanel]);
};

export const Panel = ({ children, right, closePanel, isShown }: PanelProps) => {
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, closePanel);
  const panelStyle = css`
    position: absolute;
    top: ${PANEL_SPACE_TOP}px;
    right: ${right}px;
    bottom: ${PANEL_SPACE_BOTTOM}px;
    min-height: 652px;
    padding: ${sizes.large}px ${sizes.large}px 0 0;
    padding-left: ${isShown ? sizes.large : 0}px;
    box-shadow: 0px 24px 64px rgba(0, 0, 0, 0.12);
    background: ${colors.neutralLight40};
    z-index: ${isShown ? 5 : 0};
    transition: padding-left ease 0.33s;
    overflow: hidden;
    width: ${PANEL_WIDTH}px;
    opacity: ${isShown ? 1 : 0};
    display: flex;
    flex-direction: column;
  `;

  return (
    <div css={panelStyle} ref={wrapperRef}>
      {children}
    </div>
  );
};

export const PanelHeader = ({
  title,
  options,
  onClose,
}: {
  title?: string;
  options?: ReturnType<typeof MenuItem>[];
  onClose: { (): void };
}) => {
  const { t: translate } = useTranslation();
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const ref = useRef(null);
  return (
    <Spacer orientation="row" space="small" css={panelItemsSpacingStyle}>
      <Column>
        <TypeRampV2 system="lodestar" variant="headlineMedium">
          {title}
        </TypeRampV2>
      </Column>
      <Column
        css={css`
          justify-content: flex-end;
        `}
      >
        {options ? (
          <React.Fragment>
            <PanelButton
              ref={ref}
              active={isOptionsMenuOpen}
              onClick={() => setIsOptionsMenuOpen(true)}
            >
              <KebabIcon />
            </PanelButton>
            <Menu
              aria-label={translate('common:options')}
              placement="bottom-end"
              targetElement={ref}
              isOpen={isOptionsMenuOpen}
              onCloseRequested={() => setIsOptionsMenuOpen(false)}
              id="xx"
            >
              {options}
            </Menu>
            <VerticalBar />
          </React.Fragment>
        ) : null}
        <PanelButton onClick={onClose}>
          <CloseIcon />
        </PanelButton>
      </Column>
    </Spacer>
  );
};
